import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { Sheet } from '../components/Sheet'
import {
  TextAreaField, SelectField, DateField, MoneyField,
} from '../components/Fields'
import { PhotoField } from '../components/PhotoField'
import { TenantSheet } from './TenantSheet'
import { formatMoney } from '../lib/format'
import type { Lease, LeaseStatus, Tenant, Unit } from '../lib/types'

/**
 * Creation d'un bail, et changement de loyer.
 *
 * UN CHANGEMENT DE LOYER NE MODIFIE JAMAIS LE BAIL EXISTANT : il cree
 * une nouvelle ligne qui pointe vers l'ancienne et porte le motif du
 * changement, puis l'ancien bail passe en « termine ». Son loyer et ses
 * dates restent intacts. C'est ainsi que l'historique des loyers existe
 * et qu'une augmentation non documentee ne peut pas se cacher.
 */
export function LeaseSheet({
  unit,
  units,
  tenants,
  supersedes,
  onClose,
  onSaved,
}: {
  /** Unite imposee, quand on part de la fiche d'une unite. */
  unit: Unit | null
  units: Unit[]
  tenants: Tenant[]
  /** Bail remplace, dans le cas d'un changement de loyer. */
  supersedes: Lease | null
  onClose: () => void
  /** Renvoie le bail cree, pour enchainer sur l ajout de ses pieces. */
  onSaved: (created: { leaseId: string | null; tenantId: string }) => void
}) {
  const { t } = useI18n()
  const changement = supersedes !== null

  const [unitId, setUnitId] = useState(unit?.id ?? supersedes?.unit_id ?? units[0]?.id ?? '')
  const [tenantId, setTenantId] = useState(supersedes?.tenant_id ?? tenants[0]?.id ?? '')
  const [rentTtc, setRentTtc] = useState<number | null>(supersedes?.rent_ttc ?? null)
  const [tvaRate, setTvaRate] = useState<'0' | '0.18'>(
    supersedes && Number(supersedes.tva_rate) > 0 ? '0.18' : '0',
  )
  const [deposit, setDeposit] = useState<number | null>(supersedes?.deposit_amount ?? null)
  const [advance, setAdvance] = useState<number | null>(supersedes?.advance_month ?? null)
  const [agencyFee, setAgencyFee] = useState<number | null>(supersedes?.agency_fee ?? null)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [status, setStatus] = useState<LeaseStatus>('actif')
  const [contractDoc, setContractDoc] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const [tenantSheet, setTenantSheet] = useState(false)
  const [tenantList, setTenantList] = useState(tenants)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // La TVA est comprise dans le loyer affiche. On en deduit le hors
  // taxe et le montant de TVA, et on les montre : l'arithmetique doit
  // etre verifiable a l'ecran, pas cachee dans le code.
  const taux = Number(tvaRate)
  const ttc = rentTtc ?? 0
  const ht = taux > 0 ? Math.round(ttc / (1 + taux)) : ttc
  const tva = ttc - ht

  const valide =
    unitId !== '' &&
    tenantId !== '' &&
    rentTtc !== null &&
    rentTtc > 0 &&
    startDate !== '' &&
    (!changement || reason.trim().length > 0)

  async function submit() {
    setError(null)
    setSubmitting(true)

    const { data: cree, error: e } = await supabase.from('leases').insert({
      unit_id: unitId,
      tenant_id: tenantId,
      rent_ht: ht,
      tva_rate: taux,
      rent_ttc: ttc,
      deposit_amount: deposit ?? 0,
      advance_month: advance ?? 0,
      agency_fee: agencyFee ?? 0,
      start_date: startDate,
      end_date: endDate || null,
      status,
      contract_document: contractDoc,
      supersedes_lease_id: supersedes?.id ?? null,
      change_reason: changement ? reason.trim() : null,
    })
      // L'identifiant du bail cree est renvoye pour pouvoir enchainer
      // sur l'ajout de ses pieces : un contrat fait plusieurs pages, et
      // c'est maintenant qu'on les a en main.
      .select('id')
      .single()

    if (e) {
      setSubmitting(false)
      setError(`${t('save_failed')} ${e.message}`)
      return
    }

    // L'ancien bail est cloture, mais son loyer et ses dates ne sont
    // pas touches : seul son statut change.
    if (supersedes) {
      const { error: e2 } = await supabase
        .from('leases')
        .update({ status: 'termine' })
        .eq('id', supersedes.id)
      if (e2) {
        setSubmitting(false)
        setError(`${t('save_failed')} ${e2.message}`)
        return
      }
    }

    setSubmitting(false)
    onSaved({
      leaseId: (cree as { id: string } | null)?.id ?? null,
      tenantId,
    })
  }

  if (tenantSheet) {
    return (
      <TenantSheet
        tenant={null}
        onClose={() => setTenantSheet(false)}
        onSaved={async (newId) => {
          const { data } = await supabase
            .from('tenants')
            .select('id, full_name, phone, id_type, id_number, notes')
            .order('full_name')
          setTenantList((data as Tenant[]) ?? [])
          setTenantId(newId)
          setTenantSheet(false)
        }}
      />
    )
  }

  return (
    <Sheet
      title={changement ? t('lease_change_rent') : t('lease_add')}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('save')}
      submitting={submitting}
      disabled={!valide}
      error={error}
    >
      {!unit && (
        <SelectField
          label={t('lease_unit')}
          required
          value={unitId}
          onChange={setUnitId}
          options={units.map((u) => ({ value: u.id, label: u.name }))}
        />
      )}

      <SelectField
        label={t('lease_tenant')}
        required
        value={tenantId}
        onChange={setTenantId}
        options={tenantList.map((tn) => ({ value: tn.id, label: tn.full_name }))}
      />
      <button
        type="button"
        className="btn btn--ghost btn--small"
        onClick={() => setTenantSheet(true)}
      >
        {t('tenant_add')}
      </button>

      <MoneyField
        label={t('lease_rent_ttc')}
        hint={t('lease_rent_ttc_hint')}
        required
        value={rentTtc}
        onChange={setRentTtc}
      />

      <SelectField<'0' | '0.18'>
        label={t('lease_tva')}
        hint={t('lease_tva_hint')}
        value={tvaRate}
        onChange={setTvaRate}
        options={[
          { value: '0', label: t('lease_tva_none') },
          { value: '0.18', label: t('lease_tva_18') },
        ]}
      />

      {/* Le detail du calcul, visible, pour que le chiffre soit
          verifiable sans ouvrir une calculatrice. */}
      {taux > 0 && rentTtc !== null && (
        <div className="calcbox">
          <span>
            {t('lease_ht')} <strong>{formatMoney(ht)}</strong>
          </span>
          <span>
            {t('lease_tva_amount')} <strong>{formatMoney(tva)}</strong>
          </span>
        </div>
      )}

      <DateField
        label={t('lease_start')}
        required
        value={startDate}
        onChange={setStartDate}
      />
      <DateField label={t('lease_end')} value={endDate} onChange={setEndDate} />

      <SelectField<LeaseStatus>
        label={t('lease_status')}
        value={status}
        onChange={setStatus}
        options={[
          { value: 'actif', label: t('lease_status_actif') },
          { value: 'expire', label: t('lease_status_expire') },
          { value: 'termine', label: t('lease_status_termine') },
        ]}
      />

      <MoneyField label={t('lease_deposit')} value={deposit} onChange={setDeposit} />
      <MoneyField label={t('lease_advance')} value={advance} onChange={setAdvance} />
      <MoneyField label={t('lease_agency_fee')} value={agencyFee} onChange={setAgencyFee} />

      {changement && (
        <TextAreaField
          label={t('lease_change_reason')}
          hint={t('lease_change_reason_hint')}
          required
          value={reason}
          onChange={setReason}
        />
      )}

      {/* Une seule piece ici : le bail n'existe pas encore, donc rien
          ne peut encore lui etre rattache. L'enregistrement ouvre
          aussitot la liste des pieces, ou les autres pages s'ajoutent
          une par une. */}
      <PhotoField
        label={t('lease_contract')}
        target={{ kind: 'contrat', unitId, tenantId }}
        documentId={contractDoc}
        onUploaded={setContractDoc}
      />
      {!changement && (
        <p className="field__hint">{t('lease_contract_more_after')}</p>
      )}
    </Sheet>
  )
}
